import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import moment from "moment";
import { ActionIcon, NumberInput, Select, Textarea } from "@mantine/core";
import { DatePickerInput, TimeInput } from "@mantine/dates";
import dayjs from "dayjs";
import {
  LuCalendar,
  LuChevronDown,
  LuChevronLeft,
  LuClock,
  LuMinus,
  LuPlus,
  LuX,
  LuCheck,
} from "react-icons/lu";
import { getMar, createMar, clearMarCache } from "../../services/mar";
import { getAllStaff } from "../../services/staff";
import { showError, showSuccess } from "../../utils";

// ── Status display map ─────────────────────────────────────────────────────

const STATUS_META = {
  Taken: { color: "#15803D", bg: "#DCFCE7", border: "#86EFAC", label: "Taken" },
  Missed: {
    color: "#DC2626",
    bg: "#FEF2F2",
    border: "#FECACA",
    label: "Missed",
  },
  Refused: {
    color: "#0086C9",
    bg: "#EFF6FF",
    border: "#BFDBFE",
    label: "Refused",
  },
  "Unable to Take": {
    color: "#B45309",
    bg: "#FFF8EB",
    border: "#F4D8A2",
    label: "Unable to Take",
  },
  "Out of Medication": {
    color: "#171717",
    bg: "#F4F4F5",
    border: "#E5E5E5",
    label: "Out of Med",
  },
  upcoming: {
    color: "#6B7280",
    bg: "#F4F4F5",
    border: "#E5E5E5",
    label: "Upcoming",
  },
  PRN: { color: "#7C3AED", bg: "#F3E8FF", border: "#D8B4FE", label: "PRN" },
};

const getMeta = (status) => STATUS_META[status] || STATUS_META["upcoming"];

// ── Shared helpers (mirrors desktop) ──────────────────────────────────────

const normalizeSlot = (slot) =>
  slot !== null && typeof slot === "object" ? slot.time : slot;

const getActiveSlotsForDate = (timeSlotArr, dateObj) => {
  if (!Array.isArray(timeSlotArr)) return [];
  return timeSlotArr
    .filter((slot) => {
      if (!slot?.time) return false;
      const from = slot.effectiveFrom
        ? moment(slot.effectiveFrom).startOf("day")
        : null;
      const to = slot.effectiveTo
        ? moment(slot.effectiveTo).startOf("day")
        : null;
      if (from && from.isAfter(dateObj, "day")) return false;
      if (to && to.isBefore(dateObj, "day")) return false;
      return true;
    })
    .map((s) => s.time);
};

// Returns first active slot string, or the value as-is if already a string.
const getActiveSlotForDate = (timeSlot, date) => {
  if (!Array.isArray(timeSlot)) return timeSlot ?? null;
  return (
    getActiveSlotsForDate(timeSlot, moment(date).startOf("day"))[0] ?? null
  );
};

// Resolve displayed status — mirrors desktop getSpecificMedicationStatus.
const resolveStatus = (medicationTypeArray, targetDate, targetTimeSlot) => {
  const isPastDay = moment(targetDate).isBefore(moment().startOf("day"));
  const isTodaySlotPast =
    !isPastDay &&
    (() => {
      const tSlot = Array.isArray(targetTimeSlot)
        ? targetTimeSlot[0]?.time
        : targetTimeSlot;
      if (!tSlot) return false;
      const sm = moment(tSlot, "hh:mm A");
      if (!sm.isValid()) return false;
      return moment(targetDate)
        .startOf("day")
        .set({ hour: sm.hours(), minute: sm.minutes(), second: 0 })
        .isBefore(moment());
    })();
  const isPast = isPastDay || isTodaySlotPast;

  if (typeof medicationTypeArray === "string") {
    if (medicationTypeArray.toLowerCase() === "upcoming" && isPast)
      return "Missed";
    return medicationTypeArray;
  }
  if (!Array.isArray(medicationTypeArray) || medicationTypeArray.length === 0)
    return isPast ? "Missed" : "upcoming";

  const targetDateStr = moment.utc(targetDate).format("YYYY-MM-DD");
  const tSlot = Array.isArray(targetTimeSlot)
    ? targetTimeSlot[0]?.time
    : targetTimeSlot;

  const matches = medicationTypeArray.filter((m) => {
    const isSameDate =
      moment.utc(m.date).format("YYYY-MM-DD") === targetDateStr;
    const mSlot = Array.isArray(m.timeSlot) ? m.timeSlot[0] : m.timeSlot;
    return isSameDate && (!mSlot || mSlot === tSlot);
  });

  if (matches.length > 0) {
    const status = matches[matches.length - 1].status;
    if (status?.toLowerCase() === "upcoming" && isPast) return "Missed";
    return status;
  }

  const dateOnly = medicationTypeArray.filter(
    (m) => moment.utc(m.date).format("YYYY-MM-DD") === targetDateStr,
  );
  if (dateOnly.length > 0) {
    const status = dateOnly[dateOnly.length - 1].status;
    if (status?.toLowerCase() === "upcoming" && isPast) return "Missed";
    return status;
  }

  return isPast ? "Missed" : "upcoming";
};

const resolveEntryStatus = (mar, date) => {
  const slot = getActiveSlotForDate(mar.timeSlot, date);
  return resolveStatus(mar.medicationType, date, slot);
};

// Detect PRN — handles string, array, or double-stringified JSON.
const resolveMedTypePRN = (medType) => {
  if (!medType) return false;
  if (typeof medType === "string") {
    if (medType.toUpperCase() === "PRN") return true;
    try {
      return resolveMedTypePRN(JSON.parse(medType));
    } catch {
      return false;
    }
  }
  if (Array.isArray(medType)) {
    if (medType.length === 0) return false;
    return resolveMedTypePRN(medType[medType.length - 1]?.status);
  }
  return false;
};

const isRawRecordPRN = (r) => {
  if (
    r?.form3?.medicationType !== undefined &&
    resolveMedTypePRN(r.form3.medicationType)
  )
    return true;
  return resolveMedTypePRN(r?.medicationType);
};

// ── Date select options (today + 6 days back) ─────────────────────────────

const buildDateOptions = () => {
  const options = [];
  for (let i = 6; i >= 0; i--) {
    const d = moment().subtract(i, "days");
    options.push({
      value: d.format("YYYY-MM-DD"),
      label:
        i === 0 ? `Today — ${d.format("ddd, MMM D")}` : d.format("ddd, MMM D"),
    });
  }
  return options;
};

// ── Sentinel end-date detection (mirrors desktop isSentinelEndDate) ────────

const isSentinelEndDate = (endDate) => {
  if (!endDate) return false;
  const d = new Date(endDate);
  if (isNaN(d.getTime())) return false;
  const isDecember31UTC = d.getUTCMonth() === 11 && d.getUTCDate() === 31;
  const isEndOfDaySeconds =
    d.getUTCSeconds() === 59 && d.getUTCMilliseconds() === 999;
  const isJan1UTC = d.getUTCMonth() === 0 && d.getUTCDate() === 1;
  const isEndOfPrevYearLocal =
    isJan1UTC && d.getUTCHours() < 5 && isEndOfDaySeconds;
  return (isDecember31UTC && isEndOfDaySeconds) || isEndOfPrevYearLocal;
};

// ── Full desktop-equivalent appointment builder (single date) ─────────────
//
// Mirrors the desktop appointments useMemo exactly, but scoped to one date
// instead of a full calendar range.

const buildAppointmentsForDate = (rawMar = [], clientDetails, dateStr) => {
  const targetDay = moment(dateStr).startOf("day");
  const viewStart = targetDay.clone();
  const viewEnd = targetDay.clone().endOf("day");

  // ── 1. Resolve medications list ──────────────────────────────────────────
  let medications = [];
  try {
    const stored =
      JSON.parse(localStorage.getItem("selectedClient")) || clientDetails;
    medications = stored?.form3 || [];
  } catch {
    medications = clientDetails?.form3 || [];
  }

  // Merge in any medications present in rawMar but missing from form3
  if (rawMar?.length) {
    const seen = new Set(medications.map((m) => Number(m.id)));
    rawMar.forEach((apt) => {
      if (seen.has(Number(apt.medicationId))) return;
      seen.add(Number(apt.medicationId));
      const medDef = apt.form3 || {
        id: apt.medicationId,
        timeSlots: Array.isArray(apt.timeSlot)
          ? apt.timeSlot.map(normalizeSlot)
          : [normalizeSlot(apt.timeSlot)].filter(Boolean),
        scheduleType: apt.scheduleType || "daily",
        repeatType: apt.repeatType || null,
        repeatEvery: apt.repeatEvery || 1,
        repeatOn: Array.isArray(apt.repeatOn) ? apt.repeatOn : [],
        medicationDate: apt.medicationDate || apt.date,
        medicationEndDate: apt.medicationEndDate || null,
        medicineRemaining: apt.medicineRemaining,
        totalMedicine: apt.totalMedicine,
        isUnlimited: apt.isUnlimited,
      };
      medications.push(medDef);
    });
  }

  if (!medications.length) return rawMar || [];

  // ── 2. Enrich medications with fresh schedule metadata from MAR docs ─────
  if (rawMar?.length) {
    medications = medications.map((med) => {
      const marDoc = rawMar
        .filter((r) => Number(r.medicationId) === Number(med.id))
        .sort((a, b) => new Date(b.date) - new Date(a.date))[0];
      if (!marDoc) return med;

      return {
        ...med,
        rawTimeSlot:
          Array.isArray(marDoc.timeSlot) && marDoc.timeSlot.length > 0
            ? marDoc.timeSlot
            : null,
        fallbackTimeSlotObjects:
          Array.isArray(marDoc.timeSlot) && marDoc.timeSlot.length > 0
            ? marDoc.timeSlot
            : med.fallbackTimeSlotObjects || med.timeSlot || [],
        ...(marDoc.scheduleType != null && {
          scheduleType: marDoc.scheduleType,
        }),
        ...(marDoc.repeatType !== undefined && {
          repeatType: marDoc.repeatType,
        }),
        ...(marDoc.repeatEvery != null && { repeatEvery: marDoc.repeatEvery }),
        ...(Array.isArray(marDoc.repeatOn) && { repeatOn: marDoc.repeatOn }),
        ...(marDoc.medicationDate && { medicationDate: marDoc.medicationDate }),
        ...(marDoc.medicationEndDate !== undefined && {
          medicationEndDate: marDoc.medicationEndDate,
        }),
        scheduleVersions: marDoc.scheduleVersions || med.scheduleVersions || [],
        scheduleEffectiveFrom:
          marDoc.scheduleEffectiveFrom || med.scheduleEffectiveFrom || null,
        medicineCountVersions:
          marDoc.medicineCountVersions || med.medicineCountVersions || [],
        medicineCountEffectiveFrom:
          marDoc.medicineCountEffectiveFrom ||
          med.medicineCountEffectiveFrom ||
          null,
      };
    });
  }

  // ── 3. Build existing-key set (for dedup) ────────────────────────────────
  const existingKeys = new Set();
  (rawMar || []).forEach((r) => {
    const ds = moment(r.date).utc().format("YYYY-MM-DD");
    const slots = Array.isArray(r.timeSlot)
      ? r.timeSlot.map((s) => s?.time).filter(Boolean)
      : [];
    slots.forEach((slot) =>
      existingKeys.add(`${ds}_${normalizeSlot(slot)}_${r.medicationId}`),
    );
  });

  const virtualEntries = [];

  // ── 4. buildSchedulePeriods helper ────────────────────────────────────────
  const buildSchedulePeriods = (med, medStart) => {
    const periods = [];
    if (
      Array.isArray(med.scheduleVersions) &&
      med.scheduleVersions.length > 0
    ) {
      med.scheduleVersions.forEach((version) => {
        if (!version.effectiveFrom) return;
        const anchor = moment(version.effectiveFrom).startOf("day");
        periods.push({
          from: anchor.clone(),
          to: version.effectiveTo
            ? moment(version.effectiveTo).endOf("day")
            : null,
          scheduleType: version.scheduleType || "daily",
          repeatType: version.repeatType || null,
          repeatEvery: Number(version.repeatEvery) || 1,
          repeatOn: Array.isArray(version.repeatOn) ? version.repeatOn : [],
          anchor,
        });
      });
    }
    const currentFrom = med.scheduleEffectiveFrom
      ? moment(med.scheduleEffectiveFrom).startOf("day")
      : medStart.clone();

    // For custom repeat-by-day schedules (e.g. every-other-day), the phase origin
    // must be the original medication start date (medStart), not scheduleEffectiveFrom.
    // scheduleEffectiveFrom marks when settings changed, not when the cycle started.
    // Using it as the phase anchor shifts the sequence by 1 day relative to existing
    // MAR records, producing phantom "Missed" entries on off-days (CS-28).
    const schedType = med.scheduleType || "daily";
    const repType = (med.repeatType || "day").toLowerCase();
    const repEvery = Number(med.repeatEvery) || 1;
    const phaseAnchor =
      schedType === "custom" && repType === "day" && repEvery > 1
        ? medStart.clone()
        : currentFrom.clone();

    periods.push({
      from: currentFrom,
      to: null,
      scheduleType: schedType,
      repeatType: med.repeatType || null,
      repeatEvery: repEvery,
      repeatOn: Array.isArray(med.repeatOn) ? med.repeatOn : [],
      anchor: phaseAnchor,
    });
    return periods;
  };

  const DAY_NAMES = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];

  // ── 5. Virtual entry generation ───────────────────────────────────────────
  medications.forEach((med) => {
    // Skip PRN
    const medStatus = Array.isArray(med.medicationType)
      ? med.medicationType[med.medicationType.length - 1]?.status
      : med.medicationType;
    if (resolveMedTypePRN(medStatus)) return;

    const isDiscontinued = med.discontinued || medStatus === "discontinued";

    const medStart = moment(med.medicationDate).startOf("day");
    let medEnd =
      med.medicationEndDate &&
      med.medicationEndDate !== "null" &&
      !isSentinelEndDate(med.medicationEndDate)
        ? moment(med.medicationEndDate).endOf("day")
        : viewEnd.clone();

    if (isDiscontinued) {
      const endOfYesterday = moment().subtract(1, "day").endOf("day");
      let cap = endOfYesterday;
      if (med.discontinuedOn) {
        const dayBefore = moment(med.discontinuedOn)
          .subtract(1, "day")
          .endOf("day");
        if (dayBefore.isBefore(endOfYesterday)) cap = dayBefore;
      }
      if (medEnd.isAfter(cap)) medEnd = cap;
    }

    if (medEnd.isBefore(viewStart) || medStart.isAfter(viewEnd)) return;

    const medId = med.id;
    const iterStart = moment.max(viewStart.clone(), medStart.clone());
    const iterEnd = moment.min(viewEnd.clone(), medEnd.clone());

    const sampleRecord = (rawMar || [])
      .filter((r) => Number(r.medicationId) === Number(medId))
      .sort((a, b) => new Date(b.date) - new Date(a.date))[0];

    // Resolve active slots for a given date
    const getSlotsForDate = (dateObj) => {
      if (med.rawTimeSlot)
        return getActiveSlotsForDate(med.rawTimeSlot, dateObj);
      if (
        Array.isArray(med.fallbackTimeSlotObjects) &&
        med.fallbackTimeSlotObjects.length > 0
      ) {
        const hasDateInfo = med.fallbackTimeSlotObjects.some(
          (s) => s?.effectiveFrom || s?.effectiveTo,
        );
        if (hasDateInfo)
          return getActiveSlotsForDate(med.fallbackTimeSlotObjects, dateObj);
      }
      return Array.isArray(med.timeSlots) ? med.timeSlots : [];
    };

    // Find the raw MAR doc for this med that contains a status for a specific date+slot
    const findExistingForSlot = (dateStr, slot) =>
      (rawMar || []).find(
        (r) =>
          Number(r.medicationId) === Number(medId) &&
          (Array.isArray(r.timeSlot)
            ? r.timeSlot.map(normalizeSlot).includes(slot)
            : normalizeSlot(r.timeSlot) === slot) &&
          Array.isArray(r.medicationType),
      );

    const generateEntry = (dateObj, slot) => {
      const ds = dateObj.format("YYYY-MM-DD");
      const key = `${ds}_${slot}_${medId}`;
      if (!existingKeys.has(key)) existingKeys.add(key);

      const existingForSlot = findExistingForSlot(ds, slot);

      const nowM = moment();
      const startOfToday = nowM.clone().startOf("day");
      let entryStatus;
      if (dateObj.isAfter(startOfToday, "day")) {
        entryStatus = "upcoming";
      } else if (dateObj.isSame(startOfToday, "day")) {
        const sm = slot ? moment(slot, ["hh:mm A", "HH:mm"]) : null;
        if (sm && sm.isValid()) {
          const slotToday = startOfToday
            .clone()
            .set({ hour: sm.hours(), minute: sm.minutes(), second: 0 });
          entryStatus = nowM.isSameOrAfter(slotToday) ? "Missed" : "upcoming";
        } else {
          entryStatus = "upcoming";
        }
      } else {
        entryStatus = "Missed";
      }

      let entryId = `virtual_${key}`;
      let staffFromEntry = null;
      let commentsFromEntry = null;
      let dosageFromEntry = null;
      let administeredAtFromEntry = null;
      let createdAtFromEntry = null;

      if (existingForSlot) {
        const matching = existingForSlot.medicationType.filter(
          (m) =>
            moment(m.date).format("YYYY-MM-DD") === ds &&
            (!m.timeSlot || m.timeSlot === slot),
        );
        if (matching.length > 0) {
          const last = matching[matching.length - 1];
          entryStatus = last.status;
          staffFromEntry = last.staff || null;
          commentsFromEntry = last.comments || null;
          dosageFromEntry = last.dosage ?? null;
          administeredAtFromEntry = last.administeredAt || null;
          const entryDateObj = last.date ? new Date(last.date) : null;
          const isMidnight =
            entryDateObj &&
            entryDateObj.getUTCHours() === 0 &&
            entryDateObj.getUTCMinutes() === 0 &&
            entryDateObj.getUTCSeconds() === 0 &&
            entryDateObj.getUTCMilliseconds() === 0;
          createdAtFromEntry =
            entryDateObj && !isMidnight
              ? last.date
              : existingForSlot?.createdAt || last?.date || null;
        }
        entryId = existingForSlot._id;
      }

      virtualEntries.push({
        _id: entryId,
        date: `${ds}T12:00:00.000Z`,
        timeSlot: slot,
        medicationId: medId,
        medicationType: [
          {
            status: entryStatus,
            date: `${ds}T12:00:00.000Z`,
            administeredAt: administeredAtFromEntry,
            dosage: dosageFromEntry,
            staff: staffFromEntry,
            comments: commentsFromEntry,
          },
        ],
        form3: med,
        medicineRemaining:
          med.medicineRemaining ?? sampleRecord?.medicineRemaining ?? null,
        totalMedicine: med.totalMedicine ?? sampleRecord?.totalMedicine ?? null,
        isUnlimited:
          sampleRecord?.isUnlimited ??
          med.medicineRemaining === "unlimited" ??
          false,
        client: sampleRecord?.client || null,
        staff: staffFromEntry || existingForSlot?.staff || null,
        comments: commentsFromEntry || existingForSlot?.comments || null,
        createdAt: createdAtFromEntry,
      });
    };

    const schedulePeriods = buildSchedulePeriods(med, medStart);

    schedulePeriods.forEach((period) => {
      const periodIterStart = moment.max(
        period.from.clone(),
        iterStart.clone(),
      );
      const periodIterEnd = period.to
        ? moment.min(period.to.clone(), iterEnd.clone())
        : iterEnd.clone();
      if (periodIterStart.isAfter(periodIterEnd, "day")) return;

      const pScheduleType = period.scheduleType || "daily";
      const pAnchor = period.anchor;

      if (pScheduleType === "daily") {
        getSlotsForDate(periodIterStart).forEach((slot) =>
          generateEntry(periodIterStart.clone(), slot),
        );
      } else if (pScheduleType === "custom") {
        const pRepeatType = (period.repeatType || "day").toLowerCase();
        const pRepeatEvery = Number(period.repeatEvery) || 1;
        const pRepeatOn = Array.isArray(period.repeatOn) ? period.repeatOn : [];

        if (pRepeatType === "day") {
          let cur = pAnchor.clone();
          while (cur.isSameOrBefore(periodIterEnd, "day")) {
            if (cur.isSame(periodIterStart, "day"))
              getSlotsForDate(cur).forEach((slot) =>
                generateEntry(cur.clone(), slot),
              );
            cur.add(pRepeatEvery, "day");
          }
        } else if (pRepeatType === "week") {
          const effectiveRepeatOn =
            pRepeatOn.length > 0 ? pRepeatOn : [DAY_NAMES[pAnchor.day()]];
          let weekCursor = pAnchor.clone().startOf("week");
          while (weekCursor.isSameOrBefore(periodIterEnd, "day")) {
            effectiveRepeatOn.forEach((dayName) => {
              const dayIdx = DAY_NAMES.indexOf(dayName);
              if (dayIdx === -1) return;
              const targetDate = weekCursor.clone().add(dayIdx, "days");
              if (
                targetDate.isSame(periodIterStart, "day") &&
                targetDate.isSameOrAfter(pAnchor, "day")
              ) {
                getSlotsForDate(targetDate).forEach((slot) =>
                  generateEntry(targetDate.clone(), slot),
                );
              }
            });
            weekCursor.add(pRepeatEvery, "week");
          }
        } else if (pRepeatType === "month") {
          let cur = pAnchor.clone();
          while (cur.isSameOrBefore(periodIterEnd, "day")) {
            if (cur.isSame(periodIterStart, "day"))
              getSlotsForDate(cur).forEach((slot) =>
                generateEntry(cur.clone(), slot),
              );
            cur.add(pRepeatEvery, "month");
          }
        }
      }
    });
  });

  // ── 6. Flatten raw appointments (PRN expansion + multi-slot flatten) ──────
  const flattenedAppointments = (rawMar || []).flatMap((r) => {
    if (isRawRecordPRN(r)) {
      if (Array.isArray(r.medicationType) && r.medicationType.length > 0) {
        return r.medicationType
          .filter((entry) => entry?.status === "PRN")
          .map((entry) => ({
            ...r,
            date: entry.date,
            medicationType: [entry],
            staff: entry.staff || r.staff,
            comments: entry.comments || r.comments,
          }));
      }
      return [r];
    }
    if (!Array.isArray(r.timeSlot)) return [r];
    const activeSlots = getActiveSlotsForDate(
      r.timeSlot,
      moment(r.date).startOf("day"),
    );
    if (activeSlots.length === 0) return [];
    return activeSlots.map((slotTime) => ({ ...r, timeSlot: slotTime }));
  });

  // ── 7. Historical taken/missed recovery ───────────────────────────────────
  const recoveredEntries = [];
  const recoveredKeys = new Set();

  (rawMar || []).forEach((r) => {
    if (isRawRecordPRN(r)) return;
    if (!Array.isArray(r.medicationType)) return;

    const slots = Array.isArray(r.timeSlot)
      ? r.timeSlot.map(normalizeSlot)
      : [normalizeSlot(r.timeSlot)];

    r.medicationType.forEach((entry) => {
      const status = entry?.status;
      if (!status || status === "upcoming") return;

      const entryDate = moment(entry.date);
      if (!entryDate.isValid()) return;
      if (entryDate.isBefore(viewStart) || entryDate.isAfter(viewEnd)) return;

      const ds = entryDate.utc().format("YYYY-MM-DD");
      const entrySlot = entry.timeSlot || slots[0] || "";
      const key = `${ds}_${entrySlot}_${r.medicationId}`;

      if (r.marType === "discontinued") {
        if (recoveredKeys.has(key)) return;
        recoveredKeys.add(key);
        existingKeys.add(key);
      } else {
        if (existingKeys.has(key)) return;
        existingKeys.add(key);
      }

      recoveredEntries.push({
        _id: r._id,
        date: `${ds}T12:00:00.000Z`,
        timeSlot: entrySlot,
        medicationId: r.medicationId,
        medicationType: [{ status, date: entryDate.toISOString() }],
        form3: r.form3 || null,
        medicineRemaining: r.medicineRemaining,
        totalMedicine: r.totalMedicine,
        isUnlimited: r.isUnlimited ?? false,
        client: r.client,
        staff: entry?.staff || r.staff,
        comments: entry?.comments || r.comments,
        _isHistoricalRecovery: true,
      });
    });
  });

  // Filter all three buckets to only this date
  const isOnDate = (item) =>
    moment(item.date).utc().format("YYYY-MM-DD") === dateStr;

  const all = [
    ...virtualEntries,
    ...flattenedAppointments,
    ...recoveredEntries,
  ].filter(isOnDate);

  return all;
};

// Time period buckets — same as desktop TIME_SLOTS
const TIME_PERIODS = [
  { title: "6AM - 9AM", startH: 6, endH: 9 },
  { title: "9AM - 12PM", startH: 9, endH: 12 },
  { title: "12PM - 3PM", startH: 12, endH: 15 },
  { title: "3PM - 6PM", startH: 15, endH: 18 },
  { title: "6PM - 9PM", startH: 18, endH: 21 },
  { title: "9PM - 12AM", startH: 21, endH: 0 },
  { title: "12AM - 3AM", startH: 0, endH: 3 },
  { title: "3AM - 6AM", startH: 3, endH: 6 },
];

const getTimePeriod = (slotStr) => {
  if (!slotStr) return "Other";
  const m = moment(slotStr, ["hh:mm A", "HH:mm"]);
  if (!m.isValid()) return "Other";
  const h = m.hours();
  for (const p of TIME_PERIODS) {
    if (p.endH === 0) {
      // 9PM - 12AM: 21 <= h < 24
      if (h >= p.startH) return p.title;
    } else if (p.startH < p.endH) {
      if (h >= p.startH && h < p.endH) return p.title;
    }
  }
  return "Other";
};

// Group records into time period buckets (ordered), including PRN by administered time
const groupByTimePeriod = (records, date) => {
  const groups = {};
  records.forEach((r) => {
    let timeStr;
    if (isRawRecordPRN(r)) {
      const mt = r.medicationType;
      const prnEntry = Array.isArray(mt) ? mt.find((m) => m.status === "PRN") : null;
      const rawTime = prnEntry?.date || r.createdAt;
      timeStr = rawTime ? moment(rawTime).format("hh:mm A") : null;
    } else {
      timeStr = getActiveSlotForDate(r.timeSlot, date);
    }
    const period = getTimePeriod(timeStr);
    if (!groups[period]) groups[period] = [];
    groups[period].push(r);
  });

  // Return in TIME_PERIODS order, then "Other" last
  const ordered = [];
  TIME_PERIODS.forEach((p) => {
    if (groups[p.title]?.length) ordered.push([p.title, groups[p.title]]);
  });
  if (groups["Other"]?.length) ordered.push(["Other", groups["Other"]]);
  return ordered;
};

// ── PRN reason options (mirrors desktop) ──────────────────────────────────
const PRN_REASONS = [
  "Allergies",
  "Headache",
  "Congestion",
  "Cold/Flu",
  "Constipation",
  "Cough",
  "Diarrhea",
  "Itchiness/Rash",
  "Fever",
  "Behavior",
  "Cuts/Abrasions",
  "Pain",
  "Chafing",
  "Hemorrhoids",
  "Agitation",
  "Blood Sugar",
  "Blood Pressure",
  "Anxiety and panic attacks",
  "Nausea and vomiting",
  "Sleep disturbances",
  "Asthma attacks",
  "Allergic reactions",
  "Migraine relief",
  "Muscle spasms",
  "Heartburn or acid reflux",
  "Flare-ups of chronic conditions",
  "Menstrual cramps",
  "Infections",
  "Dermatological conditions",
  "Other",
];

// ── PRN Drawer ─────────────────────────────────────────────────────────────

const PrnDrawer = ({ clientId, clientDetails, profile, onClose, onSaved }) => {
  const { staff: staffState } = useSelector((s) => s.staff);
  const { selectedClient } = useSelector((s) => s.mar);

  const form3 = clientDetails?.form3 || [];
  const meds = form3.filter(
    (m) =>
      resolveMedTypePRN(
        Array.isArray(m.medicationType)
          ? m.medicationType[m.medicationType.length - 1]?.status
          : m.medicationType,
      ) && !m.discontinued,
  );

  const [selectedMedId, setSelectedMedId] = useState(null);
  const [selectedMed, setSelectedMed] = useState(null);
  const [quantity, setQuantity] = useState("1");
  const [reason, setReason] = useState(null);
  const [otherReason, setOtherReason] = useState("");
  const [comments, setComments] = useState("");
  const timeRef = useRef();
  const [selectedStaff, setSelectedStaff] = useState(
    profile?.user?.id || profile?.user?._id || null,
  );
  const [prnCreatedAt, setPrnCreatedAt] = useState(null);
  const [useInfoOpen, setUseInfoOpen] = useState(false);
  const [instInfoOpen, setInstInfoOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const isAdminRole = profile?.user?.role !== "staff";

  // Staff options — only assigned staff (mirrors desktop)
  const assignedStaffIds = (selectedClient?.staff || clientDetails?.staff || [])
    .filter((s) => s?.isAssign === true)
    .map((s) => s?.staffId);

  const staffOptions = (staffState?.staffs || [])
    .filter((s) => assignedStaffIds.includes(s?.id))
    .map((s) => ({ value: s.id, label: s.name }));

  const medOptions = meds.map((m) => ({
    value: String(m.id || m._id),
    label: `${m.medicationName}${m.dosage ? ` ${m.dosage}${m.unit || ""}` : ""}`,
  }));

  const reasonOptions = PRN_REASONS.map((r) => ({ value: r, label: r }));

  const onMedChange = (val) => {
    setSelectedMedId(val);
    const med = meds.find((m) => String(m.id || m._id) === val);
    setSelectedMed(med || null);
    setQuantity(String(med?.medicationadministered || "1"));
  };

  const handleSave = async () => {
    if (!selectedMedId || !selectedMed)
      return showError("Please select a medication");
    if (Number(quantity) <= 0) return showError("Quantity is required");

    const createdAt = prnCreatedAt
      ? prnCreatedAt.toISOString()
      : moment().toISOString();

    const payload = {
      client: clientId,
      date: createdAt,
      createdAt,
      medicationId: selectedMed.id || selectedMed._id,
      medicationType: "PRN",
      staff: selectedStaff || profile?.user?.id || profile?.user?._id,
      comments,
      reason,
      medicationName: selectedMed.medicationName,
      dosage: Number(quantity),
      ...(reason === "Other" && { otherReason }),
    };

    setLoading(true);
    const ok = await createMar(payload, () => {});
    setLoading(false);
    if (ok !== false) {
      setPrnCreatedAt(null);
      onSaved?.();
      onClose();
    }
  };

  return (
    <div className="mm-drawer-backdrop" onClick={onClose}>
      <div className="mm-drawer" onClick={(e) => e.stopPropagation()}>
        <div className="mm-drawer-header">
          <span className="mm-drawer-title">Administer PRN Medication</span>
          <button className="mm-drawer-close" type="button" onClick={onClose}>
            <LuX size={16} />
          </button>
        </div>

        <div className="mm-drawer-body">
          {/* Staff select (admin only) */}
          {isAdminRole ? (
            <Select
              label="Select Staff"
              data={staffOptions}
              value={selectedStaff}
              onChange={setSelectedStaff}
              placeholder="Select staff…"
              searchable
              rightSection={<LuChevronDown size={14} color="#6B7280" />}
              rightSectionPointerEvents="none"
              classNames={{
                input: "mm-select-input",
                dropdown: "mm-select-dropdown",
                root: "mm-mantine-label",
              }}
              nothingFound="No staff found"
              mb={14}
            />
          ) : (
            <div className="mm-prn-staff-name">{profile?.user?.name}</div>
          )}

          {/* Medicine select */}
          <Select
            label="PRN Medicine Name"
            data={medOptions}
            value={selectedMedId}
            onChange={onMedChange}
            placeholder="Select medicine…"
            searchable
            rightSection={<LuChevronDown size={14} color="#6B7280" />}
            rightSectionPointerEvents="none"
            classNames={{
              input: "mm-select-input",
              dropdown: "mm-select-dropdown",
              root: "mm-mantine-label",
            }}
            nothingFound="No PRN medications found"
            mb={14}
          />

          {/* Use of medication + Medication instruction (collapsible, mirrors desktop accordions) */}
          <div className="mm-prn-info-row">
            <div className="mm-prn-info-block">
              <button
                type="button"
                className="mm-prn-info-toggle"
                onClick={() => setUseInfoOpen((v) => !v)}
              >
                Use of medication
                <LuChevronDown
                  size={13}
                  className={`mm-prn-info-chevron${useInfoOpen ? " mm-prn-info-chevron--open" : ""}`}
                />
              </button>
              {useInfoOpen && (
                <div className="mm-prn-info-value">
                  {selectedMed?.medicationUse || "—"}
                </div>
              )}
            </div>
            <div className="mm-prn-info-block">
              <button
                type="button"
                className="mm-prn-info-toggle"
                onClick={() => setInstInfoOpen((v) => !v)}
              >
                Medication instruction
                <LuChevronDown
                  size={13}
                  className={`mm-prn-info-chevron${instInfoOpen ? " mm-prn-info-chevron--open" : ""}`}
                />
              </button>
              {instInfoOpen && (
                <div className="mm-prn-info-value">
                  {selectedMed?.medicationInstruction || "—"}
                </div>
              )}
            </div>
          </div>

          {/* Quantity stepper */}
          <NumberInput
            label="Quantity Dispensed"
            value={Number(quantity) || 0}
            onChange={(v) => setQuantity(String(v ?? 0))}
            min={0}
            step={1}
            classNames={{ input: "mm-select-input", root: "mm-mantine-label" }}
            mb={14}
          />

          {/* Reason for Administration */}
          <Select
            label="Reason for Administration"
            data={reasonOptions}
            value={reason}
            onChange={setReason}
            placeholder="Select reason…"
            searchable
            clearable
            rightSection={<LuChevronDown size={14} color="#6B7280" />}
            rightSectionPointerEvents="none"
            classNames={{
              input: "mm-select-input",
              dropdown: "mm-select-dropdown",
              root: "mm-mantine-label",
            }}
            mb={14}
          />

          {/* Other Reason (shows when reason === "Other") */}
          {reason === "Other" && (
            <Textarea
              label="Other Reason"
              placeholder="Enter other reason here…"
              value={otherReason}
              onChange={(e) => setOtherReason(e.currentTarget.value)}
              minRows={3}
              classNames={{
                input: "mm-textarea-mantine",
                root: "mm-mantine-label",
              }}
              mb={14}
            />
          )}

          {/* Comment */}
          <Textarea
            label="Comment"
            placeholder="Enter comment here…"
            value={comments}
            onChange={(e) => setComments(e.currentTarget.value)}
            minRows={3}
            classNames={{
              input: "mm-textarea-mantine",
              root: "mm-mantine-label",
            }}
            mb={14}
          />

          {/* Administration Date + Time — mirrors desktop form.values.createdAt single-state */}
          <div className="mm-prn-datetime-row">
            <DatePickerInput
              label="Administration Date"
              placeholder="Pick a date"
              value={prnCreatedAt ? new Date(prnCreatedAt) : null}
              onChange={(newDate) => {
                if (newDate) {
                  const dt = new Date(newDate);
                  if (prnCreatedAt) {
                    const existing = new Date(prnCreatedAt);
                    dt.setHours(existing.getHours(), existing.getMinutes());
                  } else {
                    const now = new Date();
                    dt.setHours(now.getHours(), now.getMinutes());
                  }
                  setPrnCreatedAt(dt);
                } else {
                  setPrnCreatedAt(null);
                }
              }}
              maxDate={new Date()}
              icon={<LuCalendar size={14} />}
              clearable
              classNames={{
                input: "mm-select-input",
                root: "mm-mantine-label-flex",
              }}
            />
            <TimeInput
              label="Administration Time"
              value={prnCreatedAt ? dayjs(prnCreatedAt).format("HH:mm") : ""}
              onChange={(e) => {
                const newTime = e.currentTarget.value;
                if (newTime) {
                  const [hours, minutes] = newTime.split(":");
                  const dt = prnCreatedAt ? new Date(prnCreatedAt) : new Date();
                  dt.setHours(parseInt(hours), parseInt(minutes));
                  setPrnCreatedAt(new Date(dt));
                }
              }}
              ref={timeRef}
              icon={
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  size="xs"
                  onClick={() => timeRef.current?.showPicker()}
                >
                  <LuClock size={13} />
                </ActionIcon>
              }
              classNames={{
                input: "mm-select-input",
                root: "mm-mantine-label-flex",
              }}
            />
          </div>
        </div>

        <div className="mm-drawer-footer">
          <button className="mm-btn-secondary" type="button" onClick={onClose}>
            Cancel
          </button>
          <button
            className="mm-btn-primary"
            type="button"
            onClick={handleSave}
            disabled={loading || !selectedMedId}
          >
            {loading ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── buildCalendarIso — mirrors desktop exactly ────────────────────────────
const buildCalendarIso = (dateValue, timeValue) => {
  const dateKey = dateValue
    ? moment(dateValue).format("YYYY-MM-DD")
    : moment().format("YYYY-MM-DD");
  const parsedTime = timeValue
    ? moment(timeValue, ["HH:mm", "hh:mm A"], true)
    : null;
  const timePart =
    parsedTime && parsedTime.isValid()
      ? parsedTime.format("HH:mm:ss.SSS")
      : "00:00:00.000";
  return moment(`${dateKey}T${timePart}`).utc().toISOString();
};


const dateStr = (v) => {
  if (!v) return "";
  if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
  return moment(v).format("YYYY-MM-DD");
};

// ── Main MobileMarTab ───────────────────────────────────────────────────────

const MobileMarTab = ({ clientId, clientDetails, onTabSwitch }) => {
  const { mar: marList, loading } = useSelector((s) => s.mar);
  const { profile } = useSelector((s) => s.profile);
  const { staff: staffState } = useSelector((s) => s.staff);
  const { selectedClient } = useSelector((s) => s.mar);

  const dateOptions = buildDateOptions();
  const todayStr = moment().format("YYYY-MM-DD");
  const [selectedDate, setSelectedDate] = useState(todayStr);
  const [prnOpen, setPrnOpen] = useState(false);
  // mirrors desktop selectedMedicine array — compound key _id+slot
  const [selectedMedicine, setSelectedMedicine] = useState([]);

  // Inline action form state
  const isAdminRole = profile?.user?.role !== "staff";
  const defaultStaffId = profile?.user?.id || profile?.user?._id;
  const assignedStaffIds = (selectedClient?.staff || clientDetails?.staff || [])
    .filter((s) => s?.isAssign === true)
    .map((s) => s?.staffId);
  const staffOptions = (staffState?.staffs || [])
    .filter((s) => assignedStaffIds.includes(s?.id))
    .map((s) => ({ label: s.name, value: s.id }));

  const [comments, setComments] = useState("");
  const [tempDate, setTempDate] = useState(null);
  const [tempTime, setTempTime] = useState("");
  const [createdAtVal, setCreatedAtVal] = useState(null);
  const [selectedStaff, setSelectedStaff] = useState(defaultStaffId || null);
  const [saving, setSaving] = useState(false);
  const timeRef = useRef();

  const getSlotKey = (slot) =>
    Array.isArray(slot) ? (slot[0]?.time ?? null) : (slot?.time ?? slot ?? null);

  const handleDateUpdate = (newDate) => {
    setTempDate(newDate);
    if (newDate) {
      const dt = new Date(newDate);
      if (tempTime) {
        const [h, m] = tempTime.split(":");
        dt.setHours(parseInt(h), parseInt(m));
      } else {
        const now = new Date();
        dt.setHours(now.getHours(), now.getMinutes());
        setTempTime(`${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`);
      }
      setCreatedAtVal(dt);
    } else {
      setCreatedAtVal(null);
    }
  };

  const handleTimeUpdate = (newTime) => {
    setTempTime(newTime);
    if (tempDate && newTime) {
      const [h, m] = newTime.split(":");
      const dt = new Date(tempDate);
      dt.setHours(parseInt(h), parseInt(m));
      setCreatedAtVal(dt);
    }
  };

  // mirrors desktop selectMedicineHandler — compound key _id + slot
  const toggleSelect = (med) => {
    setSelectedMedicine((prev) => {
      const exists = prev.some(
        (v) => v?._id === med?._id && getSlotKey(v?.timeSlot) === getSlotKey(med?.timeSlot),
      );
      if (exists)
        return prev.filter(
          (v) => !(v?._id === med?._id && getSlotKey(v?.timeSlot) === getSlotKey(med?.timeSlot)),
        );
      return [...prev, med];
    });
  };

  const resetForm = () => {
    setSelectedMedicine([]);
    setComments("");
    setTempDate(null);
    setTempTime("");
    setCreatedAtVal(null);
  };

  // outcomeArg passed directly from button click — no status radio
  const handleSave = async (outcomeArg) => {
    if (selectedMedicine.length === 0) return showError("Please select a medicine");
    if (outcomeArg !== "Taken" && !comments) return showError("Comment is required");

    // mirrors desktop overfill check
    const uniqueMeds = selectedMedicine.filter(
      (v, i, self) => i === self.findIndex((t) => t?.medicationId === v?.medicationId),
    );
    for (const u of uniqueMeds) {
      const count = selectedMedicine
        .filter((item) => item?.medicationId === u?.medicationId)
        .reduce((sum, item) => sum + Number(item?.form3?.medicationadministered || 0), 0);
      if (outcomeArg === "Taken" && u?.form3?.medicineRemaining != null && count > u?.form3?.medicineRemaining) {
        showError(`${u?.form3?.medicationName} is overfilled`);
        return;
      }
    }

    const getCreatedAt = (item) => {
      if (createdAtVal) {
        return buildCalendarIso(createdAtVal, moment(createdAtVal).format("HH:mm"));
      }
      if (item?.timeSlot) {
        return buildCalendarIso(new Date(), getSlotKey(item.timeSlot) || moment().format("HH:mm"));
      }
      return buildCalendarIso(new Date(), dayjs().format("HH:mm"));
    };

    // mirrors desktop sortMedicine
    const sortMedicineArr = (arr) =>
      [...arr].sort((a, b) => {
        const t = (x) =>
          x?.medicationType !== "PRN"
            ? moment(x?.timeSlot, "hh:mm A").format("HH:mm")
            : moment(x?.createdAt).format("HH:mm");
        return t(a).localeCompare(t(b));
      });

    const convertTo24Hour = (time) => {
      if (!time) return "00:00";
      const timeStr = typeof time === "object" ? (time?.time ?? "") : String(time);
      const match = timeStr.match(/(\d+):(\d+)\s*(AM|PM)/i);
      if (!match) return timeStr;
      const [, hour, minute, period] = match;
      let h = parseInt(hour);
      if (period.toUpperCase() === "AM" && h === 12) h = 0;
      else if (period.toUpperCase() === "PM" && h !== 12) h += 12;
      return `${String(h).padStart(2, "0")}:${minute}`;
    };

    // mirrors desktop sortedMedicine forEach → medicationDetails
    const sortedMedicine = sortMedicineArr(selectedMedicine);
    const medicationDetails = sortedMedicine.map((med) => ({
      marId: med?._id,
      medicationId: med?.medicationId,
      dosage: med?.form3?.medicationadministered ? Number(med.form3.medicationadministered) : 0,
      slot: getSlotKey(med?.timeSlot),
      createdAt: getCreatedAt(med),
      comments: med?.comments,
    }));

    // marList raw records have timeSlot as array-of-objects with multiple slots per record.
    // For each selected medicationDetail slot, find the matching slot across all slots in each raw record.
    const allSorted = sortMedicineArr(marList || []);
    const includedRaw = [];
    medicationDetails.forEach(({ medicationId, slot }) => {
      const selectedTime = convertTo24Hour(slot);
      allSorted
        .filter((_fitem) => Number(_fitem?.medicationId) === Number(medicationId))
        .forEach((_f) => {
          // expand all slots of this raw record and find the one matching selectedTime
          const allSlots = Array.isArray(_f?.timeSlot)
            ? _f.timeSlot.map((s) => (typeof s === "object" ? s?.time : s)).filter(Boolean)
            : [getSlotKey(_f?.timeSlot)].filter(Boolean);
          const matchedSlot = allSlots.find((s) => convertTo24Hour(s) === selectedTime);
          if (matchedSlot) {
            includedRaw.push({
              marId: _f?._id,
              medicationId: _f?.medicationId,
              dosage: Number(_f?.form3?.medicationadministered) || 0,
              slot: matchedSlot,
              newMedicationType: outcomeArg,
              createdAt: getCreatedAt(_f),
            });
          }
        });
    });
    const includedDeduped = includedRaw.filter(
      (obj, idx, self) =>
        idx === self.findIndex((o) => o.marId === obj.marId && o.slot === obj.slot),
    );
    // mirrors desktop sortMars — sort included by slot time before sending
    const included = [...includedDeduped].sort((a, b) => {
      const t = (x) =>
        x?.newMedicationType !== "PRN"
          ? moment(x?.slot, "hh:mm A").format("HH:mm")
          : moment(x?.createdAt).format("HH:mm");
      return t(a).localeCompare(t(b));
    });

    const firstMar = sortedMedicine[0];
    const payload = {
      client: clientId,
      medicationType: firstMar?.form3?.medicationType === "PRN" ? "PRN" : outcomeArg,
      date: dateStr(selectedDate),
      createdAt: getCreatedAt(),
      staff: selectedStaff || defaultStaffId,
      medicationDetails,
      included,
      comments,
    };

    setSaving(true);
    const ok = await createMar(payload, () => {});
    setSaving(false);
    if (ok !== false) {
      resetForm();
      handleRefresh();
    }
  };

  useEffect(() => {
    if (!clientId) return;
    getMar(
      `daily_${moment(selectedDate).format("MM-DD-YYYY")}`,
      clientId,
      true,
    );
  }, [clientId, selectedDate]);

  useEffect(() => {
    if (!staffState?.staffs?.length) {
      getAllStaff(1, false, undefined, "");
    }
  }, []);

  // Full desktop-equivalent appointment list for selectedDate
  const allEntries = useMemo(
    () => buildAppointmentsForDate(marList || [], clientDetails, selectedDate),
    [marList, clientDetails, selectedDate],
  );

  const scheduledRecords = allEntries.filter((r) => !isRawRecordPRN(r));

  const slotGroups = groupByTimePeriod(allEntries, selectedDate);

  const handleRefresh = () => {
    clearMarCache();
    getMar(
      `daily_${moment(selectedDate).format("MM-DD-YYYY")}`,
      clientId,
      true,
    );
  };

  return (
    <div className="mc-add-form">
      {/* ── App bar (sticky, matches Note/Task tabs) ─────────────────── */}
      <div className="mc-form-app-bar">
        <button
          className="mc-icon-btn"
          type="button"
          onClick={() => onTabSwitch?.("facesheet")}
          aria-label="Back"
        >
          <LuChevronLeft size={18} strokeWidth={2.5} />
        </button>
        <div className="mc-form-app-bar-title">Medications</div>
        <div className="mc-icon-btn" style={{ visibility: "hidden" }} />
      </div>

      {/* ── Client bar (sticky, matches Note/Task tabs) ──────────────── */}
      {clientDetails && (
        <div className="mc-client-bar">
          <div className="mc-client-bar-photo">
            <div className="mc-client-bar-photo-inner">
              {(clientDetails?.name || "")
                .split(" ")
                .map((n) => n[0])
                .join("")
                .slice(0, 2)
                .toUpperCase()}
            </div>
          </div>
          <div>
            <div className="mc-client-bar-name">
              {clientDetails?.name || "Client"}
            </div>
            <div className="mc-client-bar-meta">
              <span>{clientDetails?.id?.slice(-7) || ""}</span>
            </div>
          </div>
        </div>
      )}

      <div className="mm-scroll-content">
        {/* ── Add PRN button ──────────────────────────────────────────── */}
        <button
          className="mm-add-prn-btn"
          type="button"
          onClick={() => setPrnOpen(true)}
        >
          <LuPlus size={15} />
          Add PRN
        </button>

        {/* ── Date selector ───────────────────────────────────────────── */}
        <Select
          data={dateOptions}
          value={selectedDate}
          onChange={(val) => {
            if (val) {
              setSelectedDate(val);
              resetForm();
            }
          }}
          rightSection={<LuChevronDown size={14} color="#6B7280" />}
          rightSectionPointerEvents="none"
          classNames={{
            input: "mm-date-select-input",
            dropdown: "mm-date-select-dropdown",
          }}
          allowDeselect={false}
        />

        {/* ── Loading ─────────────────────────────────────────────────── */}
        {loading && (
          <div className="mm-loading">
            <div className="mc-spinner mc-spinner--sm" />
          </div>
        )}

        {/* ── Empty state ─────────────────────────────────────────────── */}
        {!loading && allEntries.length === 0 && (
          <div className="mm-empty">
            No medications scheduled for this day
          </div>
        )}

        {/* ── All meds (scheduled + PRN) grouped by time period ───────── */}
        {!loading &&
          slotGroups.map(([periodTitle, records]) => {
            const scheduledInPeriod = records.filter((r) => !isRawRecordPRN(r));
            const takenCount = scheduledInPeriod.filter((r) => {
              const entrySlot = getActiveSlotForDate(r.timeSlot, selectedDate);
              return resolveStatus(r.medicationType, selectedDate, entrySlot) === "Taken";
            }).length;
            const prnCount = records.filter((r) => isRawRecordPRN(r)).length;
            const dueCount = scheduledInPeriod.length - takenCount;
            const countLabel = (() => {
              const parts = [];
              if (takenCount === scheduledInPeriod.length && scheduledInPeriod.length > 0)
                parts.push(`${takenCount} of ${scheduledInPeriod.length} taken`);
              else if (dueCount > 0)
                parts.push(`${dueCount} due`);
              if (prnCount > 0) parts.push(`${prnCount} PRN`);
              return parts.join(" · ");
            })();

            return (
              <div key={periodTitle} className="mm-time-section">
                <div className="mm-time-section-title">
                  <strong>{periodTitle}</strong>
                  <span className="mm-time-count">{countLabel}</span>
                </div>

                {records.map((r) => {
                  // ── PRN row ──
                  if (isRawRecordPRN(r)) {
                    const medName = r.form3?.medicationName || "—";
                    const dosage = r.form3?.dosage
                      ? `${r.form3.dosage}${r.form3.unit || ""}`
                      : null;
                    const mt = r.medicationType;
                    const prnEntry = Array.isArray(mt) ? mt.find((m) => m.status === "PRN") : null;
                    const given = prnEntry?.date
                      ? moment(prnEntry.date).format("h:mm A")
                      : r.createdAt
                        ? moment(r.createdAt).format("h:mm A")
                        : null;
                    const staffName = prnEntry?.staff?.name || r.staff?.name || null;
                    const metaParts = [dosage, staffName, given && `Given at ${given}`].filter(Boolean);
                    return (
                      <div key={`${r._id}_prn_${given}`} className="mm-med-row taken">
                        <div className="mm-med-check prn">
                          <LuCheck size={12} />
                        </div>
                        <div className="mm-med-body">
                          <div className="mm-med-name">{medName}</div>
                          {metaParts.length > 0 && (
                            <div className="mm-med-meta">{metaParts.join(" · ")}</div>
                          )}
                        </div>
                        <span className="mm-med-status prn">PRN</span>
                      </div>
                    );
                  }

                  // ── Scheduled row ──
                  const entrySlot = getActiveSlotForDate(r.timeSlot, selectedDate);
                  const st = resolveStatus(r.medicationType, selectedDate, entrySlot);
                  const meta = getMeta(st);
                  const isTaken = st === "Taken";
                  const isAlreadyDone = ["Taken","Refused","Unable to Take","Out of Medication","PRN"].includes(st);
                  const isSelected = selectedMedicine.some(
                    (v) => v?._id === r?._id && getSlotKey(v?.timeSlot) === entrySlot,
                  );
                  const canSelect = !isAlreadyDone;
                  const medName = r.form3?.medicationName || "—";
                  const dosage = r.form3?.dosage
                    ? `${r.form3.dosage}${r.form3.unit || ""}`
                    : null;
                  const route = r.form3?.ingestionMode || null;
                  const staffName = (() => {
                    const mt = r.medicationType;
                    if (Array.isArray(mt) && mt.length > 0) {
                      const match = mt.find((m) => moment(m.date).format("YYYY-MM-DD") === selectedDate);
                      return match?.staff?.name || null;
                    }
                    return r.staff?.name || null;
                  })();
                  const givenTime = (() => {
                    const mt = r.medicationType;
                    if (Array.isArray(mt) && mt.length > 0) {
                      const match = mt.find((m) => moment(m.date).format("YYYY-MM-DD") === selectedDate);
                      if (match?.administeredAt) return moment(match.administeredAt).format("h:mm A");
                      if (r.createdAt) return moment(r.createdAt).format("h:mm A");
                    }
                    return null;
                  })();
                  const metaParts = [dosage, route, staffName, givenTime].filter(Boolean);

                  return (
                    <div
                      key={`${r._id}_${entrySlot}`}
                      className={`mm-med-row${isAlreadyDone ? " taken" : ""}${isSelected ? " selected" : ""}`}
                      onClick={() => canSelect && toggleSelect(r)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => e.key === "Enter" && canSelect && toggleSelect(r)}
                    >
                      <div
                        className={`mm-med-check${isAlreadyDone ? " taken" : isSelected ? " selected" : ""}`}
                        style={isAlreadyDone ? { backgroundColor: meta.bg, borderColor: meta.border } : undefined}
                      >
                        {(isAlreadyDone || isSelected) && (
                          <LuCheck size={12} color={meta.color} />
                        )}
                      </div>
                      <div className="mm-med-body">
                        <div className="mm-med-name">{medName}</div>
                        {metaParts.length > 0 && (
                          <div className="mm-med-meta">{metaParts.join(" · ")}</div>
                        )}
                      </div>
                      {isSelected ? (
                        <span className="mm-med-status mm-med-status--selected">Selected</span>
                      ) : (
                        <span
                          className={`mm-med-status${isTaken ? " taken" : " mm-med-status-dynamic"}`}
                          style={isTaken ? undefined : {
                            "--pill-bg": meta.bg,
                            "--pill-color": meta.color,
                            "--pill-border": meta.border,
                          }}
                        >
                          {meta.label}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}

        {/* ── Footer actions ───────────────────────────────────────────── */}
        {!loading && scheduledRecords.length > 0 && (
          <>
            {/* ── Inline fields — shown when medicines are selected ──── */}
            {selectedMedicine.length > 0 && (
              <div className="mm-inline-form">
                {isAdminRole && (
                  <Select
                    label="Staff"
                    data={staffOptions}
                    value={selectedStaff}
                    onChange={setSelectedStaff}
                    placeholder="Select staff…"
                    searchable
                    rightSection={<LuChevronDown size={14} color="#6B7280" />}
                    rightSectionPointerEvents="none"
                    classNames={{ input: "mm-select-input", dropdown: "mm-select-dropdown", root: "mm-mantine-label" }}
                    nothingFound="No staff found"
                    mb={14}
                  />
                )}

                <div className="mm-prn-datetime-row" style={{ marginBottom: 14 }}>
                  <DatePickerInput
                    label="Administration Date"
                    placeholder="Pick a date"
                    value={tempDate}
                    onChange={handleDateUpdate}
                    maxDate={new Date()}
                    icon={<LuCalendar size={14} />}
                    clearable
                    classNames={{ input: "mm-select-input", root: "mm-mantine-label-flex" }}
                  />
                  <TimeInput
                    label="Administration Time"
                    value={tempTime}
                    onChange={(e) => handleTimeUpdate(e.currentTarget.value)}
                    ref={timeRef}
                    icon={
                      <ActionIcon variant="subtle" color="gray" size="xs" onClick={() => timeRef.current?.showPicker()}>
                        <LuClock size={13} />
                      </ActionIcon>
                    }
                    classNames={{ input: "mm-select-input", root: "mm-mantine-label-flex" }}
                  />
                </div>

                <Textarea
                  label="Comment"
                  placeholder="Enter comments here…"
                  value={comments}
                  onChange={(e) => setComments(e.currentTarget.value)}
                  minRows={3}
                  disabled={selectedMedicine.length === 0 || saving}
                  classNames={{ input: "mm-textarea-mantine", root: "mm-mantine-label" }}
                />
              </div>
            )}

            <div className="mm-mar-footer-actions">
              {["Refused", "Unable to Take", "Out of Medication"].map((o) => (
                <button
                  key={o}
                  className={`mm-mar-action ${o === "Refused" ? "refused" : o === "Unable to Take" ? "unable" : "outofmed"}`}
                  type="button"
                  disabled={selectedMedicine.length === 0 || saving}
                  onClick={() => handleSave(o)}
                >
                  {o}
                </button>
              ))}
            </div>

            <button
              className="mm-mar-complete-btn"
              type="button"
              disabled={selectedMedicine.length === 0 || saving}
              onClick={() => handleSave("Taken")}
            >
              <LuCheck size={15} />
              {saving ? "Saving…" : "Mark as taken"}
            </button>
          </>
        )}
      </div>

      {/* ── PRN Drawer ───────────────────────────────────────────────── */}
      {prnOpen && (
        <PrnDrawer
          clientId={clientId}
          clientDetails={clientDetails}
          profile={profile}
          onClose={() => setPrnOpen(false)}
          onSaved={handleRefresh}
        />
      )}
    </div>
  );
};

export default MobileMarTab;
